import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Box, Button, FormControl, FormLabel, Heading, Input, Text, VStack, useToast,
} from '@chakra-ui/react';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';

const API = process.env.REACT_APP_AWS_API_URL;
const AMOUNTS = [25, 50, 100];

function BuyForm({ laundryId, customerId }) {
    const stripe = useStripe();
    const elements = useElements();
    const toast = useToast();
    const navigate = useNavigate();
    const [amount, setAmount] = useState('50');
    const [recipientName, setRecipientName] = useState('');
    const [recipientEmail, setRecipientEmail] = useState('');
    const [buying, setBuying] = useState(false);
    const [sent, setSent] = useState(null);
    const authToken = localStorage.getItem('idToken');

    const buy = async () => {
        if (!stripe || !elements) return;
        const card = elements.getElement(CardElement);
        if (!card) return;
        setBuying(true);
        try {
            const start = await axios.post(`${API}/api/customer/gift-cards/checkout`, {
                laundryId, amount, recipientName, recipientEmail,
            }, { headers: { 'x-api-key': authToken } });
            if (start.data.status !== 'success' || !start.data.clientSecret) {
                toast({ title: start.data.message || 'Could not start the payment', status: 'error', duration: 4000 });
                return;
            }
            const confirm = await stripe.confirmCardPayment(start.data.clientSecret, {
                payment_method: { card },
            });
            if (confirm.error) {
                toast({ title: confirm.error.message || 'The card was declined', status: 'error', duration: 5000 });
                return;
            }
            const done = await axios.post(`${API}/api/customer/gift-cards/complete`, {
                laundryId, paymentIntentId: start.data.paymentIntentId,
            }, { headers: { 'x-api-key': authToken } });
            if (done.data.status !== 'success') {
                toast({
                    title: 'Payment went through, but the gift card was not sent',
                    description: done.data.message || 'Contact the shop with your payment receipt.',
                    status: 'error',
                    duration: 8000,
                });
                return;
            }
            setSent(done.data);
        } catch (e) {
            toast({ title: 'Could not buy the gift card', status: 'error', duration: 4000 });
        } finally {
            setBuying(false);
        }
    };

    if (sent?.card) {
        return (
            <VStack align="stretch" spacing={4}>
                <Box p={5} bg="green.50" borderRadius="md">
                    <Heading size="md" mb={2}>Gift card sent</Heading>
                    <Text>
                        {sent.emailed || sent.alreadySent
                            ? `${sent.card.code} was emailed to ${sent.card.recipientEmail}.`
                            : `We couldn't email ${sent.card.recipientEmail}. Send them this code yourself:`}
                    </Text>
                    <Text fontSize="2xl" fontWeight="bold" letterSpacing="1px" mt={2}>{sent.card.code}</Text>
                    <Text mt={2} fontSize="sm" color="gray.700">
                        They can create an account later and redeem it on their Account page. The code works once.
                    </Text>
                </Box>
                <Button variant="outline" onClick={() => navigate(`/${laundryId}/user/account`)}>Back to account</Button>
            </VStack>
        );
    }

    return (
        <VStack align="stretch" spacing={4}>
            <FormControl>
                <FormLabel>Amount</FormLabel>
                <Box display="flex" gap={2} mb={2}>
                    {AMOUNTS.map((n) => (
                        <Button key={n} size="sm" variant={String(amount) === String(n) ? 'solid' : 'outline'}
                            colorScheme="teal" onClick={() => setAmount(String(n))}>
                            ${n}
                        </Button>
                    ))}
                </Box>
                <Input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" maxW="160px" />
                <Text fontSize="xs" color="gray.500" mt={1}>$5 to $500</Text>
            </FormControl>
            <FormControl>
                <FormLabel>Friend's name</FormLabel>
                <Input value={recipientName} onChange={(e) => setRecipientName(e.target.value)} placeholder="Optional" />
            </FormControl>
            <FormControl isRequired>
                <FormLabel>Friend's email</FormLabel>
                <Input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="Where we send the code" />
            </FormControl>
            <Box p={3} borderWidth="1px" borderRadius="md" bg="white">
                <Text fontSize="sm" mb={2}>Card</Text>
                <CardElement options={{ hidePostalCode: false }} />
            </Box>
            <Button colorScheme="teal" onClick={buy} isLoading={buying} isDisabled={!stripe || !customerId}>
                Pay and send
            </Button>
        </VStack>
    );
}

export default function BuyGiftCardPage({ laundryId, customerId }) {
    return (
        <Box maxW="520px" mx="auto" p={[4, 6]}>
            <Heading size="lg" mb={2}>Send a gift card</Heading>
            <Text color="gray.600" mb={5}>
                Pay now and we email the code to your friend. They can sign up later, then redeem it on their account. The amount becomes their laundry balance.
            </Text>
            <BuyForm laundryId={laundryId} customerId={customerId} />
        </Box>
    );
}
